import type { ComponentPropsWithRef, FC, ReactNode } from 'react';
import { cloneElement, isValidElement } from 'react';

import type { ClassNameProps } from '@tripleten/cn';

import { Flex } from '../../primitives/flex/flex.tsx';
import { Slot } from '../../primitives/slot/slot.tsx';
import { VisuallyHidden } from '../../primitives/visually-hidden/visually-hidden.tsx';
import type { AccentColor } from '../../types/accent-color.ts';
import { Spinner } from '../spinner/spinner.tsx';

import { cnButton } from './button.constants.ts';

import './button.css';

import './_disabled/button_disabled.css';
import './_icon-only/button_icon-only.css';
import './_loading/button_loading.css';
import './_size/button_size_1.css';
import './_size/button_size_2.css';
import './_size/button_size_3.css';
import './_size/button_size_4.css';
import './_variant/button_variant_ghost.css';
import './_variant/button_variant_outline.css';
import './_variant/button_variant_soft.css';
import './_variant/button_variant_solid.css';

const spinnerSizeMap = { '1': '1', '2': '2', '3': '3', '4': '3' } as const;

function getSpinnerSize(size: '1' | '2' | '3' | '4'): '1' | '2' | '3' {
    return spinnerSizeMap[size];
}

function renderLoadingContents(children: ReactNode, spinnerSize: '1' | '2' | '3'): ReactNode {
    return (
        <>
            <span style={{ display: 'contents', visibility: 'hidden' }} aria-hidden>
                {children}
            </span>
            <VisuallyHidden>{children}</VisuallyHidden>
            <Flex align="center" justify="center" style={{ position: 'absolute', inset: 0 }}>
                <Spinner size={spinnerSize} />
            </Flex>
        </>
    );
}

export type ButtonProps = ClassNameProps &
    Omit<ComponentPropsWithRef<'button'>, 'color'> & {
        /**
         * Change the default rendered element for the one passed as a child, merging their props and behavior.
         */
        asChild?: boolean;
        /**
         * Controls the size of the button.
         */
        size?: '1' | '2' | '3' | '4';
        /**
         * Visual style variant.
         *
         * - `solid`: Filled with accent color.
         * - `soft`: Subtle tinted background.
         * - `outline`: Outlined, no fill.
         * - `ghost`: No background or outline.
         */
        variant?: 'solid' | 'soft' | 'outline' | 'ghost';
        /**
         * Sets the button color using the theme's accent color scale.
         */
        color?: Extract<AccentColor, 'neutral' | 'negative'>;
        /**
         * Shows a loading spinner and disables interaction.
         */
        loading?: boolean;
        /**
         * Makes the button square-shaped for icon-only actions.
         */
        iconOnly?: boolean;
    };

/**
 * Trigger for actions.
 *
 * Basic usage:
 * ```tsx
 * <Button>Save changes</Button>
 * ```
 *
 * With variant and color:
 * ```tsx
 * <Button variant="soft" color="info">Cancel</Button>
 * ```
 *
 * Loading state:
 * ```tsx
 * <Button loading>Saving…</Button>
 * ```
 *
 * Rendered as a link via asChild:
 * ```tsx
 * <Button asChild>
 *   <a href="/dashboard">Go to dashboard</a>
 * </Button>
 * ```
 */
export const Button: FC<ButtonProps> = ({
    asChild,
    size = '2',
    variant = 'solid',
    color,
    loading,
    iconOnly,
    disabled,
    className,
    children,
    ...props
}) => {
    const Component = asChild ? Slot : 'button';
    const isDisabled = disabled || loading;
    const spinnerSize = getSpinnerSize(size);

    let child: ReactNode = children;
    if (loading) {
        if (asChild && isValidElement(children)) {
            const childProps = children.props as { children?: ReactNode };
            child = cloneElement<Record<string, unknown>>(children as never, {
                ...childProps,
                children: renderLoadingContents(childProps.children, spinnerSize),
            });
        } else {
            child = renderLoadingContents(children, spinnerSize);
        }
    }

    return (
        <Component
            {...props}
            data-accent-color={color}
            disabled={isDisabled}
            className={cnButton(
                {
                    variant,
                    loading,
                    disabled: isDisabled,
                    'icon-only': iconOnly,
                    size,
                },
                [className],
            )}
        >
            {child}
        </Component>
    );
};
